import { Component, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { AbstractControl, FormControl, FormGroup, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';

import { AuthApiService } from '../../services/auth-api.service';
import { I18nService } from '../../services/i18n.service';
import { AuthCardComponent } from '../../ui/auth-card.component';
import { EMAIL_WITH_TLD_PATTERN } from '../../utils/email-validator';

const passwordPattern = /^(?=.*[A-Za-zА-Яа-яЁё])(?=.*\d).{8,}$/;

const matchPasswords: ValidatorFn = (group: AbstractControl): ValidationErrors | null => {
  const a = group.get('password')?.value;
  const b = group.get('passwordConfirm')?.value;
  return a && b && a !== b ? { mismatch: true } : null;
};

/** Format raw digits as +X (XXX) XXX-XXXX (max 11 digits). */
function formatPhone(raw: string): string {
  const digits = raw.replace(/\D/g, '').slice(0, 11);
  if (!digits.length) return '';
  let out = '+' + digits[0];
  // Каждый разделитель добавляем только когда за ним УЖЕ ЕСТЬ цифра,
  // иначе separator «прилипает» и Backspace не может стереть значение
  // (см. UKZ-54: застревало на `+7 (700)`).
  if (digits.length >= 2) out += ' (' + digits.substring(1, Math.min(4, digits.length));
  if (digits.length >= 5) out += ') ' + digits.substring(4, Math.min(7, digits.length));
  if (digits.length >= 8) out += '-' + digits.substring(7, 11);
  return out;
}

@Component({
  selector: 'auth-register',
  standalone: true,
  imports: [ReactiveFormsModule, RouterLink, AuthCardComponent],
  template: `
    <auth-card [title]="i18n.t('register.heading')" [subtitle]="i18n.t('register.subtitle')">
      @if (success()) {
        <p class="au-alert au-alert--ok">{{ i18n.t('register.success') }}</p>
      } @else {
        @if (error()) { <p class="au-alert au-alert--error">{{ error() }}</p> }

        <form [formGroup]="form" (ngSubmit)="submit()" novalidate>
          <div class="au-field">
            <label class="au-label" for="firstName">{{ i18n.t('register.first_name') }}</label>
            <input id="firstName" type="text" autocomplete="given-name"
                   class="au-input" [formControl]="form.controls.firstName">
          </div>

          <div class="au-field">
            <label class="au-label" for="lastName">{{ i18n.t('register.last_name') }}</label>
            <input id="lastName" type="text" autocomplete="family-name"
                   class="au-input" [formControl]="form.controls.lastName">
          </div>

          <div class="au-field">
            <label class="au-label" for="email">{{ i18n.t('register.email') }}</label>
            <input id="email" type="email" autocomplete="email" required
                   class="au-input"
                   [class.au-input--error]="invalid('email')"
                   [formControl]="form.controls.email">
            @if (invalid('email')) {
              <span class="au-error">{{ i18n.t('common.email_invalid') }}</span>
            }
          </div>

          <div class="au-field">
            <label class="au-label" for="phoneNumber">{{ i18n.t('register.phone') }}</label>
            <input id="phoneNumber" type="tel" autocomplete="tel"
                   class="au-input"
                   [formControl]="form.controls.phoneNumber"
                   (input)="onPhoneInput($event)"
                   placeholder="+7 (XXX) XXX-XXXX">
          </div>

          <div class="au-field">
            <label class="au-label" for="password">{{ i18n.t('register.password') }}</label>
            <input id="password" type="password" autocomplete="new-password" required minlength="8"
                   class="au-input"
                   [class.au-input--error]="invalid('password')"
                   [formControl]="form.controls.password">
            <span class="au-error" [style.color]="'var(--au-text-soft)'">{{ i18n.t('register.password_hint') }}</span>
            @if (invalid('password')) {
              <span class="au-error">{{ i18n.t('register.password_invalid') }}</span>
            }
          </div>

          <div class="au-field">
            <label class="au-label" for="passwordConfirm">{{ i18n.t('register.password_confirm') }}</label>
            <input id="passwordConfirm" type="password" autocomplete="new-password" required minlength="8"
                   class="au-input"
                   [class.au-input--error]="form.errors?.['mismatch'] && form.controls.passwordConfirm.touched"
                   [formControl]="form.controls.passwordConfirm">
            @if (form.errors?.['mismatch'] && form.controls.passwordConfirm.touched) {
              <span class="au-error">{{ i18n.t('register.password_mismatch') }}</span>
            }
          </div>

          <label class="au-checkbox">
            <input type="checkbox" [formControl]="form.controls.termsAgree">
            <span>
              <strong>{{ i18n.t('register.terms_label') }}</strong>
              <br>
              <span style="color: var(--au-text-soft); font-size: 12px;">{{ i18n.t('register.terms') }}</span>
            </span>
          </label>

          <button type="submit" class="au-button" [disabled]="loading() || !form.controls.termsAgree.value">
            {{ loading() ? i18n.t('common.loading') : i18n.t('register.submit') }}
          </button>
        </form>

        <p class="au-links">
          {{ i18n.t('register.has_account') }}
          <a [routerLink]="['/', langPath, 'login']">{{ i18n.t('register.login') }}</a>
        </p>
      }
    </auth-card>
  `,
})
export class RegisterComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private api = inject(AuthApiService);
  protected i18n = inject(I18nService);

  protected langPath: 'ru' | 'kz' = 'ru';

  readonly form = new FormGroup(
    {
      firstName: new FormControl('', { nonNullable: true }),
      lastName: new FormControl('', { nonNullable: true }),
      email: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required, Validators.email, Validators.pattern(EMAIL_WITH_TLD_PATTERN)],
      }),
      phoneNumber: new FormControl('', { nonNullable: true }),
      password: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required, Validators.pattern(passwordPattern)],
      }),
      passwordConfirm: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
      termsAgree: new FormControl(false, { nonNullable: true, validators: [Validators.requiredTrue] }),
    },
    { validators: matchPasswords },
  );

  readonly loading = signal(false);
  readonly success = signal(false);
  readonly error = signal<string>('');

  invalid(name: 'email' | 'password'): boolean {
    const c = this.form.controls[name];
    return c.invalid && (c.dirty || c.touched);
  }

  onPhoneInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const formatted = formatPhone(input.value);
    if (formatted !== input.value) input.value = formatted;
    this.form.controls.phoneNumber.setValue(formatted, { emitEvent: false });
  }

  async ngOnInit(): Promise<void> {
    const langParam = this.route.snapshot.paramMap.get('lang');
    this.langPath = langParam === 'kz' ? 'kz' : 'ru';
    await this.i18n.use(I18nService.normaliseFromUrl(langParam));
  }

  submit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.loading.set(true);
    this.error.set('');
    const v = this.form.value;
    this.api.register({
      email: v.email!,
      password: v.password!,
      firstName: v.firstName || undefined,
      lastName: v.lastName || undefined,
      phoneNumber: v.phoneNumber || undefined,
    }).subscribe({
      next: (resp) => {
        this.success.set(true);
        // auth-server sends back the OAuth handoff URL (?just_registered=true)
        if (resp.redirectUrl) {
          window.location.href = resp.redirectUrl;
        }
      },
      error: (err: HttpErrorResponse) => {
        this.loading.set(false);
        this.error.set(err.error?.message || this.i18n.t('register.error'));
      },
    });
  }
}
